import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import {
  FiSearch,
  FiDownload,
  FiUpload,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
} from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";

import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import { formatAmountWithCommas } from "../../../../Utils/utils";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import { getBOQById, saveBOQ, generateNextBOQNo, initialBOQList } from "./boqService";
import BOQItemDrawer from "./BOQItemDrawer";

const BOQForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();

  // Route state or ID
  const routeState = location.state || {};
  const boqId = id || routeState.Id || routeState.BOQNo;

  // Active BOQ Data
  const [boq, setBoq] = useState(null);
  const [sections, setSections] = useState([]);
  const [status, setStatus] = useState("Draft");
  const [contingencyPct, setContingencyPct] = useState(5);
  const [collapsedSections, setCollapsedSections] = useState({});

  // Toolbar state
  const [searchQuery, setSearchQuery] = useState("");
  const [sectionFilter, setSectionFilter] = useState("ALL");

  // Drawer state & resizer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(480);
  const [isDragging, setIsDragging] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeHighlightedItemId, setActiveHighlightedItemId] = useState(null);

  // New Section Modal
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");

  // Confirmation Modals
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);

  // Split-pane resizer handler
  const handleMouseDownResize = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const startX = e.clientX;
    const startWidth = drawerWidth;

    const handleMouseMove = (moveEvent) => {
      const delta = startX - moveEvent.clientX;
      const minWidth = 360;
      const maxWidth = Math.max(minWidth, Math.min(850, window.innerWidth - 380));
      const newWidth = Math.max(minWidth, Math.min(startWidth + delta, maxWidth));
      setDrawerWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Load BOQ on mount
  useEffect(() => {
    let current = null;
    if (boqId) {
      current = getBOQById(boqId);
    }
    if (!current) {
      // If adding new, or not found, initialize new or use first mock as fallback
      if (location.pathname.includes("/add")) {
        current = {
          Id: `boq-${Date.now()}`,
          BOQNo: generateNextBOQNo(),
          BOQName: "New Construction BOQ",
          Project: "ADF Colony",
          BuildingPhase: "Tower A",
          Version: "V1",
          Amount: 0,
          AwardedValue: 0,
          Status: "Draft",
          CreatedDate: "17 Jul 2026",
          ContingencyPct: 5,
          Sections: [
            {
              id: "sec-1",
              title: "A. Earthworks",
              items: [],
            },
            {
              id: "sec-2",
              title: "B. Concrete Works",
              items: [],
            },
          ],
        };
      } else {
        current = initialBOQList[0];
      }
    }

    setBoq(current);
    setSections(current.Sections ? JSON.parse(JSON.stringify(current.Sections)) : []);
    setStatus(current.Status || "Draft");
    setContingencyPct(current.ContingencyPct || 5);
  }, [boqId, location.pathname]);

  // Derived Totals
  const { totalBaseAmount, totalItemsCount, sectionsCount } = useMemo(() => {
    let sum = 0;
    let count = 0;
    sections.forEach((sec) => {
      (sec.items || []).forEach((item) => {
        count += 1;
        const itemAmt = Number(item.amount) || Number(item.qty || 0) * Number(item.unitRate || 0);
        sum += itemAmt;
      });
    });
    return {
      totalBaseAmount: sum,
      totalItemsCount: count,
      sectionsCount: sections.length,
    };
  }, [sections]);

  const contingencyAmount = useMemo(() => {
    return Math.round((totalBaseAmount * (Number(contingencyPct) || 0)) / 100);
  }, [totalBaseAmount, contingencyPct]);

  const grandTotalAmount = useMemo(() => {
    return totalBaseAmount + contingencyAmount;
  }, [totalBaseAmount, contingencyAmount]);

  const awardedValue = boq?.AwardedValue || 15600000;
  const remainingBudget = Math.max(0, totalBaseAmount - awardedValue);

  // Filtered Sections and Items
  const filteredSections = useMemo(() => {
    return sections
      .filter((sec) => {
        if (sectionFilter === "ALL") {
          return true;
        }
        return sec.title === sectionFilter;
      })
      .map((sec) => {
        if (!searchQuery.trim()) {
          return sec;
        }
        const q = searchQuery.toLowerCase();
        const matchingItems = (sec.items || []).filter(
          (item) =>
            (item.itemCode || "").toLowerCase().includes(q) ||
            (item.description || "").toLowerCase().includes(q) ||
            (item.specification || "").toLowerCase().includes(q),
        );
        return {
          ...sec,
          items: matchingItems,
        };
      })
      .filter((sec) => (sec.items && sec.items.length > 0) || !searchQuery.trim());
  }, [sections, sectionFilter, searchQuery]);

  // Collapse / Expand Toggles
  const toggleSectionCollapse = (secId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [secId]: !prev[secId],
    }));
  };

  const handleExpandAll = () => {
    setCollapsedSections({});
  };

  const handleCollapseAll = () => {
    const all = {};
    sections.forEach((s) => {
      all[s.id] = true;
    });
    setCollapsedSections(all);
  };

  // Add Item Handler (from Drawer)
  const handleOpenAddItem = (sectionTitle = "") => {
    setEditingItem(null);
    setActiveHighlightedItemId(null);
    setDrawerOpen(true);
  };

  const handleOpenEditItem = (item, sectionTitle) => {
    setEditingItem({
      ...item,
      sectionTitle,
    });
    setActiveHighlightedItemId(item.id);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingItem(null);
    setActiveHighlightedItemId(null);
  };

  // Save Item from Drawer
  const handleSaveItemFromDrawer = (itemData, addAnother = false) => {
    setSections((prevSections) => {
      const copy = JSON.parse(JSON.stringify(prevSections));
      const targetSecTitle = itemData.sectionTitle;

      // Find if section already exists
      let targetSec = copy.find((s) => s.title === targetSecTitle);
      if (!targetSec) {
        // Create new section
        targetSec = {
          id: `sec-${Date.now()}`,
          title: targetSecTitle,
          items: [],
        };
        copy.push(targetSec);
      }

      // Check if updating existing item or adding new
      const existingItemIndex = (targetSec.items || []).findIndex((i) => i.id === itemData.id);

      // If it moved from another section, remove it from old section first
      copy.forEach((sec) => {
        if (sec.title !== targetSecTitle) {
          sec.items = (sec.items || []).filter((i) => i.id !== itemData.id);
        }
      });

      if (existingItemIndex >= 0) {
        targetSec.items[existingItemIndex] = itemData;
      } else {
        targetSec.items = targetSec.items || [];
        targetSec.items.push(itemData);
      }

      return copy;
    });

    if (!addAnother) {
      setActiveHighlightedItemId(itemData.id);
    }
  };

  // Delete Item
  const handleConfirmDeleteItem = () => {
    if (!deleteItemTarget) {
      return;
    }
    setSections((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.forEach((sec) => {
        sec.items = (sec.items || []).filter((i) => i.id !== deleteItemTarget.id);
      });
      return copy;
    });
    toast.success(`Item "${deleteItemTarget.itemCode}" deleted`);
    setDeleteItemTarget(null);
    if (activeHighlightedItemId === deleteItemTarget.id) {
      setActiveHighlightedItemId(null);
    }
  };

  // Add Section Modal Handler
  const handleCreateNewSection = () => {
    if (!newSectionTitle.trim()) {
      toast.error("Please enter a section title");
      return;
    }
    const exists = sections.some(
      (s) => s.title.toLowerCase() === newSectionTitle.trim().toLowerCase(),
    );
    if (exists) {
      toast.error("A section with this title already exists");
      return;
    }

    const newSec = {
      id: `sec-${Date.now()}`,
      title: newSectionTitle.trim(),
      items: [],
    };
    setSections((prev) => [...prev, newSec]);
    toast.success(`Section "${newSectionTitle.trim()}" added`);
    setNewSectionTitle("");
    setShowAddSectionModal(false);
  };

  // Save Draft to LocalStorage
  const handleSaveDraft = () => {
    if (!boq) {
      return;
    }
    const updatedBOQ = {
      ...boq,
      Sections: sections,
      Status: "Draft",
      ContingencyPct: Number(contingencyPct) || 5,
    };
    const saved = saveBOQ(updatedBOQ);
    setBoq(saved);
    setStatus("Draft");
    toast.success(`BOQ "${saved.BOQNo}" saved as Draft`);
  };

  // Submit for Approval Confirmation Flow
  const handleOpenSubmitConfirm = () => {
    if (!boq) {
      return;
    }
    if (totalItemsCount === 0) {
      toast.error("Please add at least one line item before submitting for approval");
      return;
    }
    setShowSubmitConfirmModal(true);
  };

  const handleConfirmSubmitForApproval = () => {
    if (!boq) {
      return;
    }
    const updatedBOQ = {
      ...boq,
      Sections: sections,
      Status: "Pending Approval",
      ContingencyPct: Number(contingencyPct) || 5,
    };
    const saved = saveBOQ(updatedBOQ);
    setBoq(saved);
    setStatus("Pending Approval");
    setShowSubmitConfirmModal(false);
    toast.success(`BOQ "${saved.BOQNo}" submitted for approval`);
  };

  // Export CSV
  const handleExportCSV = () => {
    let csv =
      "Section,Item Code,Description,Specification,Qty,Unit,Unit Rate (PKR),Amount (PKR),Rate Source\n";
    sections.forEach((sec) => {
      (sec.items || []).forEach((item) => {
        csv += `"${sec.title}","${item.itemCode}","${item.description}","${item.specification || ""}",${item.qty},"${item.unit}",${item.unitRate},${item.amount},"${item.rateSource}"\n`;
      });
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${boq?.BOQNo || "BOQ"}_Items.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.info("BOQ items exported to CSV");
  };

  if (!boq) {
    return <div className="p-4 text-center">Loading BOQ details...</div>;
  }

  return (
    <div className="pb-5 app_font">
      <div
        style={{
          display: "flex",
          width: "100%",
          alignItems: "flex-start",
          position: "relative",
        }}
      >
        {/* Left Column: Form Content */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            transition: isDragging ? "none" : "all 0.15s ease",
          }}
        >
          {/* ─── Standard ERP Page Heading ─── */}
          <div className="row">
            <div className="col-md-12 text-left page_heading">
              <div className="d-flex align-items-center">
                <FaArrowLeft
                  className="mr-2 cur_sor"
                  onClick={() => history.push("/works/bill-of-quantities")}
                />
                <h4 className="m-0">
                  {boq ? `${boq.BOQNo} — ${boq.BOQName}` : "Create Bill of Quantities (BOQ)"}
                </h4>
              </div>
            </div>
          </div>

          {/* ─── Metadata Details Bar ─── */}
          <div
            className="d-flex flex-wrap align-items-center justify-content-between pt-2 pb-2 mb-3"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="text-muted" style={{ fontSize: "12.5px" }}>
              Project: <strong style={{ color: "#111827" }}>{boq.Project}</strong> | Building:{" "}
              <strong style={{ color: "#111827" }}>{boq.BuildingPhase}</strong> | Version:{" "}
              <strong style={{ color: "#111827" }}>{boq.Version}</strong> | Created:{" "}
              <span>{boq.CreatedDate}</span>
            </div>
            <div>
              <StatusChip status={status} type={statusChipType(status)} />
            </div>
          </div>

          {/* ─── Top 5 Metric Cards matching image 2 ────────────────────────────── */}
          <div className="row mb-3">
            {/* Card 1: Total BOQ Budget */}
            <div className="col-md col-sm-6 mb-2">
              <div
                className="card p-3 shadow-sm"
                style={{ borderRadius: "8px", border: "1px solid #e5e7eb", height: "100%" }}
              >
                <div className="text-muted" style={{ fontSize: "12px", fontWeight: 500 }}>
                  Total BOQ Budget
                </div>
                <div
                  style={{ fontSize: "20px", fontWeight: 700, color: "#111827", marginTop: "4px" }}
                >
                  PKR {formatAmountWithCommas(totalBaseAmount)}
                </div>
              </div>
            </div>

            {/* Card 2: Sections */}
            <div className="col-md col-sm-6 mb-2">
              <div
                className="card p-3 shadow-sm"
                style={{ borderRadius: "8px", border: "1px solid #e5e7eb", height: "100%" }}
              >
                <div className="text-muted" style={{ fontSize: "12px", fontWeight: 500 }}>
                  Sections
                </div>
                <div
                  style={{ fontSize: "20px", fontWeight: 700, color: "#111827", marginTop: "4px" }}
                >
                  {sectionsCount}
                </div>
              </div>
            </div>

            {/* Card 3: Line Items */}
            <div className="col-md col-sm-6 mb-2">
              <div
                className="card p-3 shadow-sm"
                style={{ borderRadius: "8px", border: "1px solid #e5e7eb", height: "100%" }}
              >
                <div className="text-muted" style={{ fontSize: "12px", fontWeight: 500 }}>
                  Line Items
                </div>
                <div
                  style={{ fontSize: "20px", fontWeight: 700, color: "#111827", marginTop: "4px" }}
                >
                  {totalItemsCount}
                </div>
              </div>
            </div>

            {/* Card 4: Awarded Value (WOs) */}
            <div className="col-md col-sm-6 mb-2">
              <div
                className="card p-3 shadow-sm"
                style={{ borderRadius: "8px", border: "1px solid #e5e7eb", height: "100%" }}
              >
                <div className="text-muted" style={{ fontSize: "12px", fontWeight: 500 }}>
                  Awarded Value (WOs)
                </div>
                <div
                  style={{ fontSize: "20px", fontWeight: 700, color: "#111827", marginTop: "4px" }}
                >
                  PKR {formatAmountWithCommas(awardedValue)}
                </div>
              </div>
            </div>

            {/* Card 5: Remaining Budget */}
            <div className="col-md col-sm-6 mb-2">
              <div
                className="card p-3 shadow-sm"
                style={{ borderRadius: "8px", border: "1px solid #e5e7eb", height: "100%" }}
              >
                <div className="text-muted" style={{ fontSize: "12px", fontWeight: 500 }}>
                  Remaining Budget
                </div>
                <div
                  style={{ fontSize: "20px", fontWeight: 700, color: "#056363", marginTop: "4px" }}
                >
                  PKR {formatAmountWithCommas(remainingBudget)}
                </div>
              </div>
            </div>
          </div>

          {/* ─── Table Controls Toolbar matching image 2 ───────────────────────── */}
          <div
            className="d-flex flex-wrap align-items-center justify-content-between p-2 mb-3 bg-white"
            style={{
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              gap: "10px",
            }}
          >
            <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
              {/* Search Items */}
              <div className="position-relative" style={{ width: "220px" }}>
                <FiSearch
                  size={14}
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9ca3af",
                  }}
                />
                <input
                  type="text"
                  className="form-control form-control-sm pl-4"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    borderRadius: "6px",
                    fontSize: "12.5px",
                    height: "34px",
                    paddingLeft: "30px",
                  }}
                />
              </div>

              {/* Section Filter */}
              <select
                className="form-control form-control-sm"
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                style={{ width: "160px", borderRadius: "6px", fontSize: "12.5px", height: "34px" }}
              >
                <option value="ALL">All Sections</option>
                {sections.map((s, idx) => (
                  <option key={idx} value={s.title}>
                    {s.title}
                  </option>
                ))}
              </select>

              {/* Expand / Collapse All */}
              <button
                type="button"
                className="btn btn-sm btn-light border px-2"
                onClick={handleExpandAll}
                style={{ fontSize: "12px", height: "34px", borderRadius: "6px" }}
              >
                Expand All
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light border px-2"
                onClick={handleCollapseAll}
                style={{ fontSize: "12px", height: "34px", borderRadius: "6px" }}
              >
                Collapse All
              </button>

              {/* Import / Export */}
              <button
                type="button"
                className="btn btn-sm btn-light border px-2 d-inline-flex align-items-center"
                onClick={() => toast.info("Excel template import dialog")}
                style={{ fontSize: "12px", height: "34px", borderRadius: "6px", gap: "4px" }}
              >
                <FiUpload size={13} /> Import Excel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light border px-2 d-inline-flex align-items-center"
                onClick={handleExportCSV}
                style={{ fontSize: "12px", height: "34px", borderRadius: "6px", gap: "4px" }}
              >
                <FiDownload size={13} /> Export
              </button>
            </div>

            {/* Right side toolbar action buttons using ActionButton */}
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <ActionButton
                text="+ Section"
                variant="info"
                onClick={() => setShowAddSectionModal(true)}
              />
              <ActionButton text="+ Item" newBtn="true" onClick={() => handleOpenAddItem()} />
            </div>
          </div>

          {/* ─── Hierarchical BOQ Items Table matching image 2 ───────────────────── */}
          <div
            className="card shadow-sm mb-3"
            style={{ borderRadius: "8px", border: "1px solid #e5e7eb", overflow: "hidden" }}
          >
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                <thead
                  style={{
                    backgroundColor: "#f8fafc",
                    color: "#374151",
                    borderBottom: "2px solid #e5e7eb",
                  }}
                >
                  <tr>
                    <th style={{ width: "40px", textAlign: "center" }}>#</th>
                    <th style={{ width: "90px" }}>Item Code</th>
                    <th style={{ minWidth: "160px" }}>Description</th>
                    <th style={{ minWidth: "220px" }}>Specification</th>
                    <th style={{ width: "90px", textAlign: "right" }}>Qty</th>
                    <th style={{ width: "70px", textAlign: "center" }}>Unit</th>
                    <th style={{ width: "120px", textAlign: "right" }}>Unit Rate (PKR)</th>
                    <th style={{ width: "130px", textAlign: "right" }}>Amount (PKR)</th>
                    <th style={{ width: "100px", textAlign: "center" }}>Rate Source</th>
                    <th style={{ width: "90px", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSections.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-5 text-muted">
                        No sections or items found matching your filters.
                      </td>
                    </tr>
                  ) : (
                    filteredSections.map((sec) => {
                      const isCollapsed = Boolean(collapsedSections[sec.id]);
                      // Calculate section total
                      const sectionTotal = (sec.items || []).reduce((acc, curr) => {
                        const amt =
                          Number(curr.amount) || Number(curr.qty || 0) * Number(curr.unitRate || 0);
                        return acc + amt;
                      }, 0);

                      return (
                        <React.Fragment key={sec.id}>
                          {/* Section Header Row */}
                          <tr
                            style={{
                              backgroundColor: "#f1f5f9",
                              fontWeight: 700,
                              cursor: "pointer",
                              borderTop: "1px solid #e2e8f0",
                              borderBottom: "1px solid #cbd5e1",
                            }}
                            onClick={() => toggleSectionCollapse(sec.id)}
                          >
                            <td colSpan={7} className="py-2 px-3">
                              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                                {isCollapsed ? (
                                  <FiChevronRight size={16} />
                                ) : (
                                  <FiChevronDown size={16} />
                                )}
                                <span style={{ color: "#0f172a", fontSize: "13.5px" }}>
                                  {sec.title}
                                </span>
                                <span
                                  className="badge badge-light border text-muted px-2 py-0.5"
                                  style={{ fontSize: "11px", fontWeight: 500 }}
                                >
                                  {(sec.items || []).length} items
                                </span>
                              </div>
                            </td>
                            <td
                              className="py-2 px-3 text-right"
                              style={{ color: "#0f172a", fontSize: "13.5px" }}
                            >
                              {formatAmountWithCommas(sectionTotal)}
                            </td>
                            <td colSpan={2} className="py-2 px-3 text-right">
                              <button
                                type="button"
                                className="btn btn-link p-0 text-muted"
                                title="Add item to this section"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAddItem(sec.title);
                                }}
                                style={{
                                  fontSize: "12px",
                                  textDecoration: "none",
                                  color: "#056363",
                                }}
                              >
                                + Add Item
                              </button>
                            </td>
                          </tr>

                          {/* Items Rows */}
                          {!isCollapsed &&
                            ((sec.items || []).length === 0 ? (
                              <tr>
                                <td
                                  colSpan={10}
                                  className="text-center py-3 text-muted"
                                  style={{ fontStyle: "italic", fontSize: "12px" }}
                                >
                                  No items in this section.{" "}
                                  <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    onClick={() => handleOpenAddItem(sec.title)}
                                    style={{ fontSize: "12px", color: "#056363" }}
                                  >
                                    Add first item
                                  </button>
                                </td>
                              </tr>
                            ) : (
                              sec.items.map((item, itemIdx) => {
                                const isHighlighted = activeHighlightedItemId === item.id;
                                const itemAmt =
                                  Number(item.amount) ||
                                  Number(item.qty || 0) * Number(item.unitRate || 0);

                                return (
                                  <tr
                                    key={item.id}
                                    style={{
                                      backgroundColor: isHighlighted ? "#f0fdfa" : "transparent",
                                      borderLeft: isHighlighted
                                        ? "4px solid #056363"
                                        : "4px solid transparent",
                                      transition: "background-color 0.15s ease",
                                    }}
                                  >
                                    <td
                                      className="text-center text-muted"
                                      style={{ verticalAlign: "middle" }}
                                    >
                                      {itemIdx + 1}
                                    </td>
                                    <td
                                      style={{
                                        verticalAlign: "middle",
                                        fontWeight: 600,
                                        color: "#1e293b",
                                      }}
                                    >
                                      {item.itemCode}
                                    </td>
                                    <td
                                      style={{
                                        verticalAlign: "middle",
                                        fontWeight: 500,
                                        color: "#0f172a",
                                      }}
                                    >
                                      <div>{item.description}</div>
                                      {item.consumptions && item.consumptions.length > 0 && (
                                        <div
                                          className="d-inline-flex align-items-center mt-1"
                                          style={{
                                            fontSize: "11px",
                                            color: "#0369a1",
                                            backgroundColor: "#f0f9ff",
                                            padding: "1px 6px",
                                            borderRadius: "4px",
                                            border: "1px solid #bae6fd",
                                          }}
                                        >
                                          ✓ {item.consumptions.length} expected material(s) linked
                                        </div>
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        verticalAlign: "middle",
                                        color: "#475569",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {item.specification || "—"}
                                    </td>
                                    <td
                                      className="text-right"
                                      style={{ verticalAlign: "middle", fontWeight: 500 }}
                                    >
                                      {Number(item.qty).toFixed(2)}
                                    </td>
                                    <td
                                      className="text-center text-muted"
                                      style={{ verticalAlign: "middle" }}
                                    >
                                      {item.unit}
                                    </td>
                                    <td
                                      className="text-right"
                                      style={{ verticalAlign: "middle", color: "#334155" }}
                                    >
                                      {formatAmountWithCommas(item.unitRate)}
                                    </td>
                                    <td
                                      className="text-right font-weight-bold"
                                      style={{ verticalAlign: "middle", color: "#056363" }}
                                    >
                                      {formatAmountWithCommas(itemAmt)}
                                    </td>
                                    <td className="text-center" style={{ verticalAlign: "middle" }}>
                                      <span
                                        className="badge"
                                        style={{
                                          backgroundColor:
                                            item.rateSource === "Library" ? "#f1f5f9" : "#fef3c7",
                                          color:
                                            item.rateSource === "Library" ? "#475569" : "#92400e",
                                          border:
                                            item.rateSource === "Library"
                                              ? "1px solid #cbd5e1"
                                              : "1px solid #fde68a",
                                          fontSize: "11px",
                                          padding: "3px 7px",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {item.rateSource || "Library"}
                                      </span>
                                    </td>
                                    <td className="text-center" style={{ verticalAlign: "middle" }}>
                                      <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ gap: "10px" }}
                                      >
                                        <FiEdit2
                                          title="Edit Item in Drawer"
                                          size="1.1em"
                                          style={{ cursor: "pointer", color: "#4e545a" }}
                                          onClick={() => handleOpenEditItem(item, sec.title)}
                                        />
                                        <MdDeleteForever
                                          title="Delete Item"
                                          size="1.2em"
                                          className="cur_sor"
                                          style={{ cursor: "pointer", color: "#c0392b" }}
                                          onClick={() => setDeleteItemTarget(item)}
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ))}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ─── BOQ Cost Summary Footer Box matching image 2 ────────────────── */}
            <div
              className="p-3"
              style={{
                borderTop: "2px solid #e2e8f0",
                backgroundColor: "#f8fafc",
              }}
            >
              <div className="row align-items-center">
                <div className="col-md-6 mb-2 mb-md-0">
                  <h6 className="m-0 font-weight-bold" style={{ color: "#0f172a" }}>
                    BOQ Cost Summary
                  </h6>
                  <div className="text-muted" style={{ fontSize: "12px", marginTop: "2px" }}>
                    Calculated across {sectionsCount} sections and {totalItemsCount} line items
                  </div>
                </div>

                <div className="col-md-6 text-md-right">
                  <div
                    style={{
                      display: "inline-block",
                      textAlign: "right",
                      minWidth: "260px",
                    }}
                  >
                    <div
                      className="d-flex justify-content-between mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      <span className="text-muted">Base BOQ Value:</span>
                      <strong style={{ color: "#1e293b" }}>
                        PKR {formatAmountWithCommas(totalBaseAmount)}
                      </strong>
                    </div>

                    <div
                      className="d-flex justify-content-between align-items-center mb-1"
                      style={{ fontSize: "13px" }}
                    >
                      <span className="text-muted d-inline-flex align-items-center">
                        Contingency ({contingencyPct}%):
                      </span>
                      <span style={{ color: "#475569" }}>
                        PKR {formatAmountWithCommas(contingencyAmount)}
                      </span>
                    </div>

                    <div
                      className="d-flex justify-content-between pt-2 mt-2"
                      style={{
                        borderTop: "1px solid #cbd5e1",
                        fontSize: "15px",
                      }}
                    >
                      <strong style={{ color: "#0f172a" }}>Total:</strong>
                      <strong style={{ color: "#056363", fontSize: "16px" }}>
                        PKR {formatAmountWithCommas(grandTotalAmount)}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Bottom Submit Actions using standard ActionButton ─── */}
          <div className="float-right mt-4 mr-md-3 mb-5" style={{ display: "flex", gap: "10px" }}>
            <ActionButton
              text="Cancel"
              variant="cancel"
              onClick={() => history.push("/works/bill-of-quantities")}
            />
            <ActionButton text="Save Draft" variant="grey" onClick={handleSaveDraft} />
            <ActionButton
              text="Submit for Approval"
              variant="save"
              onClick={handleOpenSubmitConfirm}
            />
          </div>
          <div className="clearfix" />
        </div>

        {/* ─── Resizer Splitter Bar (only rendered when drawer is open) ──────── */}
        {drawerOpen && (
          <div
            onMouseDown={handleMouseDownResize}
            style={{
              width: "12px",
              minHeight: "750px",
              alignSelf: "stretch",
              cursor: "col-resize",
              backgroundColor: isDragging ? "#056363" : "#f1f5f9",
              borderLeft: isDragging ? "2px solid #056363" : "1px solid #e2e8f0",
              borderRight: isDragging ? "2px solid #056363" : "1px solid #e2e8f0",
              position: "relative",
              zIndex: 10,
              margin: "0 8px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              userSelect: "none",
              transition: isDragging
                ? "none"
                : "background-color 0.15s ease, border-color 0.15s ease",
              boxShadow: isDragging ? "0 0 8px rgba(5, 99, 99, 0.35)" : "none",
            }}
            title="Click and drag to resize drawer"
          >
            <div
              style={{
                width: "4px",
                height: "40px",
                borderRadius: "3px",
                backgroundColor: isDragging ? "#ffffff" : "#94a3b8",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                alignItems: "center",
                padding: "4px 0",
              }}
            >
              <span
                style={{
                  width: "2px",
                  height: "2px",
                  borderRadius: "50%",
                  backgroundColor: isDragging ? "#ffffff" : "#475569",
                }}
              />
              <span
                style={{
                  width: "2px",
                  height: "2px",
                  borderRadius: "50%",
                  backgroundColor: isDragging ? "#ffffff" : "#475569",
                }}
              />
              <span
                style={{
                  width: "2px",
                  height: "2px",
                  borderRadius: "50%",
                  backgroundColor: isDragging ? "#ffffff" : "#475569",
                }}
              />
            </div>
          </div>
        )}

        {/* ─── Right Column: BOQItemDrawer side-panel ─────────────────────────── */}
        {drawerOpen && (
          <div
            style={{
              width: `${drawerWidth}px`,
              minWidth: "360px",
              maxWidth: "850px",
              flexShrink: 0,
              position: "sticky",
              top: "16px",
              height: "calc(100vh - 32px)",
              display: "flex",
              flexDirection: "column",
              zIndex: 5,
            }}
          >
            <BOQItemDrawer
              open={drawerOpen}
              onClose={handleDrawerClose}
              onSave={handleSaveItemFromDrawer}
              editItem={editingItem}
              existingSections={sections}
              defaultSection={editingItem?.sectionTitle || sections[0]?.title}
            />
          </div>
        )}
      </div>

      {/* ─── Add Section Modal (ERP Standard UI) ────────────────────────────── */}
      {showAddSectionModal && (
        <div style={{ display: "block", zIndex: 1100 }} className="modalscreen">
          <div className="modal-content-cat_voucher6" style={{ maxWidth: "500px" }}>
            <div className="row">
              <div className="col-12 page_heading">
                <h4 className="text-center pt-2">Add New Section</h4>
                <span
                  className="close pr-2"
                  onClick={() => {
                    setShowAddSectionModal(false);
                    setNewSectionTitle("");
                  }}
                >
                  &times;
                </span>
              </div>
            </div>

            <div className="row p-3" style={{ width: "100%" }}>
              <div className="col-12">
                <div className="row align-items-center">
                  <div className="col-md-3 text-left p-0">
                    <label className="input_label m-0">Title :</label>
                  </div>
                  <div className="col-md-9 p-0">
                    <input
                      type="text"
                      className="input_styling ml-md-2"
                      style={{ width: "95%" }}
                      placeholder="e.g. G. Wood & Aluminum Fixtures"
                      value={newSectionTitle}
                      onChange={(e) => setNewSectionTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateNewSection();
                        }
                      }}
                      autoFocus
                    />
                    <span className="estaric">*</span>
                  </div>
                </div>

                <div className="float-right mt-4">
                  <ActionButton
                    text="Cancel"
                    variant="cancel"
                    type="button"
                    onClick={() => {
                      setShowAddSectionModal(false);
                      setNewSectionTitle("");
                    }}
                  />
                  <ActionButton
                    text="Save"
                    variant="save"
                    type="button"
                    onClick={handleCreateNewSection}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Submit for Approval Confirmation Modal ──────────────────────────── */}
      <DeleteConfirmModal
        open={showSubmitConfirmModal}
        onClose={() => setShowSubmitConfirmModal(false)}
        onConfirm={handleConfirmSubmitForApproval}
        heading="Submit BOQ for Approval"
        message={`Are you sure you want to submit BOQ "${boq?.BOQNo} — ${boq?.BOQName}" for approval? Once submitted, the BOQ status will be updated to Pending Approval.`}
        actionText="Submit for Approval"
        actionVariant="save"
      />

      {/* ─── Delete Item Confirmation Modal ─────────────────────────────────── */}
      <DeleteConfirmModal
        open={Boolean(deleteItemTarget)}
        onClose={() => setDeleteItemTarget(null)}
        onConfirm={handleConfirmDeleteItem}
        heading="Delete Line Item"
        message={`Are you sure you want to delete item "${deleteItemTarget?.itemCode}: ${deleteItemTarget?.description}"?`}
      />
    </div>
  );
};

export default BOQForm;
